/**
 * Output preview section for MappingPhase (column-mapping-ui spec, "Output
 * preview table"). Thin composition wrapper — the row-building logic lives in
 * lib/build-mapping-preview.ts (pure, reuses the shared @ooh/contracts
 * transform/validation engine).
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { RowError } from '@ooh/contracts';
import { formatTemplate } from '@ooh/ui/lib/utils';
import { buildMappingPreview } from '../lib/build-mapping-preview';
import { collectMappedFieldKeys } from '../lib/collect-mapped-field-keys';
import { MappingTransformsPreviewTable } from './mapping-transforms-preview-table';
import { resolveValidationMsg, type TFn } from './validation-tables';
import { buildConstantMappingItems } from './mapping-phase-constant-editor';
import type { ColumnMappingItem, FieldOptionItem } from './mapping-phase';

/** Translates a RowError via the shared validation-tables.tsx resolver: a code-driven
 * message resolves via t('validation.errors.CODE', params); @ooh/contracts's pre-built
 * English `message` is only a fallback for the rare code-less error. */
function resolveErrorMessage(t: TFn) {
  return (error: RowError): string =>
    resolveValidationMsg(t, error.code, error.message, error.params);
}

export interface MappingPhasePreviewSectionProps {
  /** Should be the override-filtered `state.visibleMappings`, not the raw `state.mappings` — a
   * column collapsed behind a file-level media-type override must not show up in the preview. */
  mappings: ColumnMappingItem[];
  /** Missing-required fields resolved via the constant editor (column-mapping-ui: "Constant and
   * value-map editors") — fed in as synthetic mapping entries so a constant-satisfied required
   * field appears in the preview and counts toward row validity instead of showing as an error. */
  constantAssignments: Record<string, string>;
  columnSamples: Record<string, string[]> | undefined;
  fieldOptions: FieldOptionItem[];
  overrideActive: boolean;
  /** Mirrors use-mapping-phase-state.ts's supplierCoveredByCombobox — true when the operator
   * picked a supplier via the combobox (or a saved mapping already implies one), so the
   * 'supplier'/'name' required-field check must not surface a false error in the preview
   * (item #1: "supplier chosen via picker still shows 'supplier required' in preview"). */
  supplierCovered: boolean;
  /** Template with a `{count}` token. */
  headingTemplate: string;
  regionDerivedWarning: string;
}

export function MappingPhasePreviewSection({
  mappings,
  constantAssignments,
  columnSamples,
  fieldOptions,
  overrideActive,
  supplierCovered,
  headingTemplate,
  regionDerivedWarning,
}: MappingPhasePreviewSectionProps) {
  const { t } = useTranslation('mediaImport');
  const combinedMappings = React.useMemo(() => {
    // Drop a stale constant assignment once a real column mapping now covers the same
    // field key — otherwise the synthetic constant entry can win over the real mapping
    // by ordering in downstream row-building.
    const mappedKeys = collectMappedFieldKeys(mappings);
    const liveConstantAssignments = Object.fromEntries(
      Object.entries(constantAssignments).filter(([fieldKey]) => !mappedKeys.has(fieldKey)),
    );
    return [...mappings, ...buildConstantMappingItems(liveConstantAssignments)];
  }, [mappings, constantAssignments]);
  const preview = React.useMemo(
    () =>
      buildMappingPreview(
        combinedMappings,
        columnSamples,
        fieldOptions,
        {
          regionDerivedWarning,
          // i18next's TFunction has an overloaded signature incompatible with the
          // simplified TFn alias used for dynamic, code-driven translation keys.
          resolveErrorMessage: resolveErrorMessage(t as unknown as TFn),
        },
        overrideActive,
        supplierCovered,
      ),
    [
      combinedMappings,
      columnSamples,
      fieldOptions,
      regionDerivedWarning,
      overrideActive,
      supplierCovered,
      t,
    ],
  );
  if (preview.rows.length === 0) return null;
  return (
    <MappingTransformsPreviewTable
      heading={formatTemplate(headingTemplate, { count: preview.rows.length })}
      columns={preview.columns}
      rows={preview.rows}
    />
  );
}
